import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DollarOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    InfoCircleOutlined
} from "@ant-design/icons";
import {
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Input,
    Modal,
    Row,
    Space,
    Tag,
    Typography,
    notification,
} from "antd";
import React, { useState, useCallback, useMemo, memo } from "react";
import { STATUS_COLORS, formatDate } from "./utils";
import { updateApplicantPaymentStatusApiRequest } from "../../../../api/applicant";

const { Title, Text } = Typography;
const { TextArea } = Input;

// Separate RejectModal component to prevent re-rendering of the main modal when typing
const RejectModal = memo(({ visible, onCancel, onConfirm, loading }) => {
    const [rejectReason, setRejectReason] = useState("");
    const [validationError, setValidationError] = useState("");

    // Reset state when modal opens/closes
    React.useEffect(() => {
        if (!visible) {
            setRejectReason("");
            setValidationError("");
        }
    }, [visible]);

    // Memoize handlers to prevent recreation on each render
    const handleReasonChange = useCallback((e) => {
        const value = e.target.value;
        setRejectReason(value);

        // Live validation
        if (!value.trim()) {
            setValidationError("Please provide a reason for rejection");
        } else if (value.trim().length < 10) {
            setValidationError("Reason must be at least 10 characters long");
        } else {
            setValidationError("");
        }
    }, []);

    const handleCancel = useCallback(() => {
        setRejectReason("");
        setValidationError("");
        onCancel();
    }, [onCancel]);

    const handleConfirm = useCallback(() => {
        if (!rejectReason.trim()) {
            setValidationError("Please provide a reason for rejection");
            notification.warning({
                message: "Required Field Missing",
                description: "Please provide a reason for rejection before proceeding.",
                duration: 4,
            });
            return;
        }

        if (rejectReason.trim().length < 10) {
            setValidationError("Reason must be at least 10 characters long");
            notification.warning({
                message: "Rejection Reason Too Short",
                description: "Please provide a more detailed reason (at least 10 characters).",
                duration: 4,
            });
            return;
        }

        onConfirm(rejectReason);
    }, [rejectReason, onConfirm]);

    // Memoize the footer to prevent recreation on each render
    const modalFooter = useMemo(() => [
        <Button key="cancel" onClick={handleCancel}>
            Cancel
        </Button>,
        <Button
            key="confirm"
            danger
            type="primary"
            onClick={handleConfirm}
            loading={loading}
            disabled={!rejectReason.trim() || rejectReason.trim().length < 10}
        >
            Reject Application
        </Button>,
    ], [handleCancel, handleConfirm, rejectReason, loading]);

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <ExclamationCircleOutlined
                        className="mr-2"
                        style={{ color: "#ff4d4f" }}
                    />
                    <span className="text-lg font-medium">Confirm Rejection</span>
                </div>
            }
            open={visible}
            onCancel={handleCancel}
            footer={modalFooter}
            width={550}
            centered
            maskClosable={false}
            keyboard={true}
            className="rejection-modal"
        >
            <div className="p-4">
                <div className="bg-red-50 p-4 rounded-md mb-4 border border-red-200">
                    <p className="flex items-center">
                        <InfoCircleOutlined className="mr-2 text-red-500" />
                        <span>This action will reject the applicant's payment information.</span>
                    </p>
                </div>

                <div className="mb-4">
                    <Text strong className="text-base">Please provide a reason for rejection:</Text>
                    <div className="text-xs text-gray-500 mb-2">
                        This information will be recorded and may be shared with the applicant.
                    </div>
                    <TextArea
                        rows={4}
                        placeholder="Enter detailed reason for rejection (minimum 10 characters)"
                        value={rejectReason}
                        onChange={handleReasonChange}
                        maxLength={500}
                        showCount
                        status={validationError ? "error" : ""}
                        style={{ marginTop: "8px" }}
                        autoFocus
                        aria-label="Rejection reason"
                    />
                    {validationError && (
                        <div className="text-red-500 mt-1 text-sm">
                            <ExclamationCircleOutlined className="mr-1" />
                            {validationError}
                        </div>
                    )}
                </div>

                <Divider className="my-3" />

                <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                    <div className="flex items-start">
                        <FileTextOutlined className="mr-2 mt-1 text-blue-500" />
                        <div>
                            <Text strong>Important Note:</Text>
                            <p className="text-sm text-gray-600 mt-1">
                                This reason will be recorded in the system and may be displayed to the applicant.
                                Please ensure your explanation is clear, professional, and specific.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
});

// Memoized StatusTag component to prevent unnecessary re-renders
const StatusTag = memo(({ status }) => {
    const color = STATUS_COLORS[status] || "default";
    return <Tag color={color}>{status}</Tag>;
});

// Main PaymentInfoModal component
const PaymentInfoModal = ({ visible, applicant, onClose, onSuccess }) => {
    // State management
    const [updateLoading, setUpdateLoading] = useState(false);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    // Add state to track the applicant's payment status
    const [paymentStatus, setPaymentStatus] = useState({});

    // Initialize payment status from applicant data when it changes
    React.useEffect(() => {
        if (applicant) {
            setPaymentStatus({
                reservationFeePaymentStepStatus: applicant.reservationFeePaymentStepStatus || "Pending",
                approvedExamFeeStatus: applicant.approvedExamFeeStatus || "Pending"
            });
        }
    }, [applicant]);

    // Memoized handlers to prevent recreation on each render
    const showRejectConfirmation = useCallback(() => {
        setRejectModalVisible(true);
    }, []);

    const handleRejectCancel = useCallback(() => {
        setRejectModalVisible(false);
    }, []);

    // Handle status update with optional rejection message
    const handleStatusUpdate = useCallback(async (newStatus, rejectionMessage = "") => {
        if (!applicant) return; // Safety check

        console.group(
            `🔄 ${newStatus.toUpperCase()} PAYMENT ACTION INITIATED FROM PAYMENT MODAL`
        );
        console.log("Applicant ID:", applicant._id);
        console.log("Applicant ID (readable):", applicant.applicantID);
        console.log(
            "Applicant Name:",
            `${applicant.firstName} ${applicant.lastName}`
        );
        console.log("Current Payment Status:", applicant.reservationFeePaymentStepStatus || "Unknown");
        console.log("Target Status:", newStatus);
        if (rejectionMessage) {
            console.log("Rejection Message:", rejectionMessage);
        }
        console.groupEnd();

        setUpdateLoading(true);
        try {
            // Prepare status data for local state update
            const statusData = {
                reservationFeePaymentStepStatus: newStatus,
                approvedExamFeeStatus: newStatus,
                updatedAt: new Date().toISOString(),
                ...(newStatus === "Rejected" && rejectionMessage ? {
                    paymentRejectionReason: rejectionMessage,
                    paymentRejectionDate: new Date().toISOString()
                } : {}),
                ...(newStatus === "Approved" ? {
                    paymentApprovalDate: new Date().toISOString()
                } : {})
            };
            
            console.log("Payment Status Data:", statusData);
            
            // Call the API to update the payment status
            const response = await updateApplicantPaymentStatusApiRequest(
                applicant._id,
                newStatus,
                newStatus === "Rejected" ? rejectionMessage : null
            );
            
            console.log("API Response:", response);
            
            // Update local state to reflect the changes immediately
            setPaymentStatus(prev => ({
                ...prev,
                ...statusData
            }));

            // Close the rejection modal if it was open
            if (newStatus === "Rejected") {
                setRejectModalVisible(false);
            }

            // Show a success notification
            notification.success({
                message: "Success",
                description: `Payment ${newStatus === "Approved" ? "approved" : "rejected"} successfully`,
                duration: 3,
            });
            
            // Call onSuccess callback to trigger data refresh in parent component
            if (onSuccess && typeof onSuccess === 'function') {
                console.log('Triggering parent refresh callback');
                onSuccess();
            } else {
                console.log('No onSuccess callback provided, cannot refresh data');
            }
        } catch (error) {
            console.error("Error updating payment status from modal:", error);
            notification.error({
                message: "Error",
                description: error.message || "Failed to update payment status",
                duration: 4,
            });
        } finally {
            setUpdateLoading(false);
        }
    }, [applicant, onSuccess]); // Added onSuccess to dependency array

    const handleRejectConfirm = useCallback((reason) => {
        handleStatusUpdate("Rejected", reason);
    }, [handleStatusUpdate]);

    // Memoize the modal footer to prevent recreation on each render
    const modalFooter = useMemo(() => {
        if (!applicant) return [];

        return [
            <Space key="actions" size="middle">
                <Button
                    key="reject"
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={showRejectConfirmation}
                    loading={updateLoading && applicant?.admissionApprovalAdminStatus !== "Approved"}
                    disabled={applicant?.admissionApprovalAdminStatus === "Rejected"}
                    size="large"
                >
                    Reject
                </Button>
                <Button
                    key="approve"
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => handleStatusUpdate("Approved")}
                    loading={updateLoading && applicant?.admissionApprovalAdminStatus !== "Rejected"}
                    disabled={applicant?.admissionApprovalAdminStatus === "Approved"}
                    style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                    size="large"
                >
                    Approve
                </Button>
                <Button key="close" onClick={onClose} size="large">
                    Close
                </Button>
            </Space>,
        ];
    }, [showRejectConfirmation, handleStatusUpdate, updateLoading, applicant, onClose]);

    // Early return if no applicant data
    if (!applicant) return null;

    return (
        <>
            {/* Main Payment Information Modal */}
            <Modal
                title={
                    <div className="flex items-center">
                        <DollarOutlined className="mr-2 text-green-500" />
                        <span className="text-lg font-medium">Payment Information</span>
                    </div>
                }
                open={visible}
                onCancel={onClose}
                footer={modalFooter}
                width={800}
                centered
                className="payment-info-modal"
            >
                <div className="p-4">
                    <Card
                        className="mb-5 border-l-4 shadow-sm"
                        style={{ borderLeftColor: "#1890ff" }}
                        bodyStyle={{ padding: "16px" }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <div className="text-center mb-2">
                                    <Title level={4} style={{ margin: 0, color: "#1890ff" }} className="mb-2">
                                        {applicant.firstName} {applicant.middleName}{" "}
                                        {applicant.lastName}
                                    </Title>
                                    <Tag color="blue" className="text-base px-4 py-1">
                                        {applicant.applicantID}
                                    </Tag>
                                </div>
                            </Col>
                        </Row>
                    </Card>

                    <Descriptions
                        title={<Title level={5}>Exam Information</Title>}
                        bordered
                        size="middle"
                        column={{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
                        className="mb-5"
                        labelStyle={{ fontWeight: "bold" }}
                    >
                        <Descriptions.Item label="Exam Date">
                            {formatDate(applicant.approvedExamDate) || "Not scheduled"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Exam Time">
                            {applicant.approvedExamTime || "Not scheduled"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Exam Room">
                            {applicant.approvedExamRoom || "Not assigned"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Exam Fee Amount">
                            <Text strong>₱{applicant.approvedExamFeeAmount || 0}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Exam Fee Status">
                            <StatusTag status={paymentStatus.approvedExamFeeStatus || applicant.approvedExamFeeStatus || "Unpaid"} />
                        </Descriptions.Item>
                        <Descriptions.Item label="Exam Result">
                            <StatusTag status={applicant.approvedExamInterviewResult || "Pending"} />
                        </Descriptions.Item>
                    </Descriptions>

                    <Descriptions
                        title={<Title level={5}>Payment Information</Title>}
                        bordered
                        size="middle"
                        column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
                        className="mb-5"
                        labelStyle={{ fontWeight: "bold" }}
                    >
                        <Descriptions.Item label="Reservation Fee Paid">
                            <Text strong>₱{applicant.reservationFeeAmountPaid || 0}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Reservation Fee Status">
                            <StatusTag status={paymentStatus.reservationFeePaymentStepStatus || applicant.reservationFeePaymentStepStatus || "Incomplete"} />
                        </Descriptions.Item>
                    </Descriptions>

                    <Descriptions
                        title={<Title level={5}>Payment Decision</Title>}
                        bordered
                        size="middle"
                        column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                        className="mb-4"
                        labelStyle={{ fontWeight: "bold" }}
                    >
                        <Descriptions.Item label="Current Status">
                            <div className="flex items-center justify-center">
                                <StatusTag status={applicant.approvedExamInterviewResult || "Pending"} />
                            </div>
                        </Descriptions.Item>
                    </Descriptions>

                    <div className="mt-5 text-gray-500 text-sm">
                        <InfoCircleOutlined className="mr-1" />
                        <span>Use the buttons below to approve or reject this payment information.</span>
                    </div>
                </div>
            </Modal>

            {/* Use the separate RejectModal component for better performance */}
            <RejectModal
                visible={rejectModalVisible}
                onCancel={handleRejectCancel}
                onConfirm={handleRejectConfirm}
                loading={updateLoading}
            />
        </>
    );
};

export default memo(PaymentInfoModal);

